import { NotificationHandler } from "components/Notification";
import { AuthenticationCtx } from "components/providers/AuthCtx";
import { ApiError } from "data/api_error";
import { Message } from "data/message";
import { ApiResponse } from "data/response";
import { env } from "env/env";

export const api = `${env.apiEndpoint}/${env.apiVersion}`
export const controllerApi = `${env.controllerUrl}/${env.controllerVersion}`

export interface RequestCtx {
    url: string;
    authCtx?: AuthenticationCtx;
}

export function getHeaders(authCtx?: AuthenticationCtx) {
    const headers = {
        "Content-Type": "application/json",
    };
    if (authCtx?.session?.access_token === undefined) {
        return headers;
    }

    return {
        ...headers,
        "Authorization": `Bearer ${authCtx.session.access_token}`,
    };
}

function responseToError(res: Response) {
    return res.text().then(text => {
        throw new ApiError(res, text, res.statusText);
    });
}

export function handleResponse<T>(res: Response): Promise<ApiResponse<T>> {
    if (!res.ok) {
        return responseToError(res);
    }

    return res.json() as Promise<ApiResponse<T>>;
}

export function handleBlobResponse(res: Response): Promise<Blob> {
    if (!res.ok) {
        return responseToError(res);
    }

    return res.blob() as Promise<Blob>;
}

export function handleError(error: Error, notificationHandler: NotificationHandler|null) {
    if (notificationHandler !== null) {
        if (!(error instanceof ApiError)) {
            notificationHandler({
                message: error.message,
                type: "error",
            });
        } else {
            handleApiError(error, notificationHandler);
        }
    }
}

function handleApiError(error: ApiError, notificationHandler: NotificationHandler) {
    const data = error.data.data as Message;
    notificationHandler({
        message: data.message,
        type: "error",
    });
}

export function onError(e: Error): Error {
    if (!(e instanceof ApiError))
        return new Error("Erreur inattendue");

    return e;
}

export function get<T>(ctx: RequestCtx): Promise<ApiResponse<T>> {
    const opts = {
        headers: getHeaders(ctx.authCtx),
    }

    return fetch(ctx.url, opts).then((res) => handleResponse<T>(res)).catch((e) => {
        throw onError(e);
    });
}

export function post<T>(ctx: RequestCtx, body: object = {}): Promise<ApiResponse<T>> {
    const opts = {
        method: "POST",
        headers: getHeaders(ctx.authCtx),
        body: JSON.stringify(body)
    }

    return fetch(ctx.url, opts).then(res => handleResponse<T>(res)).catch((e) => {
        throw onError(e);
    });
}

export function put<T>(ctx: RequestCtx, body: object = {}): Promise<ApiResponse<T>> {
    const opts = {
        method: "PUT",
        headers: getHeaders(ctx.authCtx),
        body: JSON.stringify(body)
    }

    return fetch(ctx.url, opts).then(res => handleResponse<T>(res)).catch((e) => {
        throw onError(e);
    });
}

export function del<T>(ctx: RequestCtx): Promise<ApiResponse<T>> {
    const opts = {
        method: "DELETE",
        headers: getHeaders(ctx.authCtx),
    }

    return fetch(ctx.url, opts).then(res => handleResponse<T>(res)).catch((e) => {
        throw onError(e);
    });
}
